import {
  Box,
  CircularProgress,
  styled,
  TextField,
  Typography,
} from "@material-ui/core";
import { useSearchParams } from "react-router-dom";
import { useBillingCodesEnabled } from "../_lib/billing/use-billing-codes-enabled";
import { EPOCUS_TEXT_SECONDARY, rem } from "../_lib/epocus-palette";
import { selectActiveProgram, useMyPrograms } from "../_lib/use-active-program";
import { useEpocusBillingDayQuery } from "./_lib/__generated__/billing-operations.gql";
import { BillerExportCard } from "./_lib/biller-export-card";
import { BillingBanner } from "./_lib/billing-banner";
import {
  BillingCard,
  BillingCardHeader,
  BillingCardTitle,
  BillingHint,
  BillingPageHeading,
  BillingPageRoot,
} from "./_lib/billing-chrome";
import { BillingKpis } from "./_lib/billing-kpis";
import { BillingStudyRow } from "./_lib/billing-study-row";
import {
  formatServiceDay,
  isIsoDay,
  readIsoDay,
  toServiceDate,
} from "./_lib/service-day";

const LoadingRow = styled(Box)({
  display: "flex",
  justifyContent: "center",
  padding: 24,
});

const EmptyNote = styled(Typography)({
  fontSize: rem(13),
  color: EPOCUS_TEXT_SECONDARY,
  padding: "16px",
});

export const EpocusBillingPage: FC = () => {
  const [params, setParams] = useSearchParams();
  const isoDay = readIsoDay(params);

  const billingCodesEnabled = useBillingCodesEnabled();
  const { programs, loading: programsLoading } = useMyPrograms();
  const program = selectActiveProgram(programs);
  const programId = program?.id ?? null;

  const { data, loading, error } = useEpocusBillingDayQuery({
    variables: {
      programId: programId ?? "",
      serviceDate: toServiceDate(isoDay),
    },
    skip: !programId,
    fetchPolicy: "cache-and-network",
  });

  const day = data?.epocusBillingDay;
  const billable = day?.billable ?? [];
  const heldBack = day?.heldBack ?? [];
  const rows = [...billable, ...heldBack];

  return (
    <BillingPageRoot>
      <BillingPageHeading
        title="Billing — end of day"
        subtitle={`${formatServiceDay(isoDay)} · billable studies for your billing company`}
      />

      <BillingBanner>
        <b>Billing add-on is on for this program.</b> The day's compliant
        reports are assembled here. Nothing incomplete is ever included.
      </BillingBanner>

      <TextField
        type="date"
        label="Service date"
        value={isoDay}
        variant="outlined"
        size="small"
        style={{ maxWidth: 220 }}
        InputLabelProps={{ shrink: true }}
        onChange={(event) => {
          const value = event.target.value;
          if (isIsoDay(value)) setParams({ date: value });
        }}
      />

      {billingCodesEnabled && programId && (
        <BillerExportCard programId={programId} />
      )}

      <BillingKpis
        billableCount={billable.length}
        heldBackCount={heldBack.length}
        showFees={billingCodesEnabled}
        feeTotals={day?.feeTotals}
      />

      <BillingCard>
        <BillingCardHeader>
          <BillingCardTitle>Ready to bill</BillingCardTitle>
          <BillingHint>
            {billable.length} ready{" "}
            {billable.length === 1 ? "report" : "reports"}
          </BillingHint>
        </BillingCardHeader>

        {!programId && programsLoading ? (
          <LoadingRow>
            <CircularProgress aria-label="Loading your program" />
          </LoadingRow>
        ) : loading && !day ? (
          <LoadingRow>
            <CircularProgress aria-label="Loading the day's studies" />
          </LoadingRow>
        ) : error ? (
          <EmptyNote>Couldn't load this day's billing.</EmptyNote>
        ) : rows.length === 0 ? (
          <EmptyNote>No studies recorded on this day.</EmptyNote>
        ) : (
          rows.map((row) => (
            <BillingStudyRow
              key={row.examId}
              row={row}
              showFee={billingCodesEnabled}
            />
          ))
        )}
      </BillingCard>

      <BillingHint>
        Studies that are not signed, or are missing the patient's name, date of
        birth or MRN, a date of service, the rendering provider's NPI, or a
        needle-in-target image for a guided procedure, are held back and never
        exported. Non-billable studies are left out altogether.
      </BillingHint>
    </BillingPageRoot>
  );
};
